import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../common/Icon';
import { apiFormRequest, apiErrorText } from '../../utils';

/* The person behind the business card.

   Two ways in and they meet in the same fields: photograph the card and let the
   server read it, or type. The scan only ever PRE-FILLS — the rep confirms every
   value before anything is written, because an email read wrong from a glossy card
   becomes a permanent, unique, undeliverable Partner row.

   The card photo is sent, read and dropped; it is never stored. */
const PersonFields = ({ value, onChange, buId, showRole = true }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scanned, setScanned] = useState(false);

  const set = (key) => (e) => onChange({ ...value, [key]: e.target.value });

  const handleScan = async (event) => {
    const file = event.target.files?.[0];
    // Let the same card be photographed again after a bad read.
    event.target.value = '';
    if (!file) return;

    setScanning(true);
    setScanError(null);
    try {
      const form = new FormData();
      form.append('image', file);
      if (buId) form.append('bu_id', buId);
      const { card } = await apiFormRequest('/sales/scan-card', form);
      onChange({
        ...value,
        // Only overwrite what the card actually showed: a rep who typed the email
        // before scanning must not lose it to a field the model left blank.
        first_name: card.first_name || value.first_name || '',
        name: card.name || value.name || '',
        email: card.email || value.email || '',
        phone: card.phone || value.phone || '',
        mobile: card.mobile || value.mobile || '',
        job_title: card.job_title || value.job_title || '',
        seniority: card.seniority || value.seniority || '',
        department: card.department || value.department || '',
        role: card.suggested_role || value.role || 'lead',
        company_name: card.company_name || value.company_name || '',
      });
      setScanned(true);
    } catch (err) {
      // Read the key off the body. The route answers with a reason worth saying —
      // scanning is not switched on here, that file is not a photo we can read —
      // and `err.message` alone would print the bare key at the rep.
      setScanError(apiErrorText(t, err, 'salesScanFailed'));
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="sales-person">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="d-none"
        onChange={handleScan}
      />
      <button
        type="button"
        className="sales-scan"
        onClick={() => fileRef.current?.click()}
        disabled={scanning}
      >
        <Icon name="camera" />
        <span>{scanning ? t('salesScanning') : t('salesScanCard')}</span>
      </button>
      {scanError && <p className="sales-error">{scanError}</p>}
      {scanned && !scanError && <p className="sales-hint">{t('salesScanCheck')}</p>}

      <div className="sales-row">
        <input className="sales-input" placeholder={t('firstName')}
               value={value.first_name || ''} onChange={set('first_name')} />
        <input className="sales-input" placeholder={t('lastName')}
               value={value.name || ''} onChange={set('name')} />
      </div>
      <input className="sales-input" type="email" placeholder={t('email')}
             value={value.email || ''} onChange={set('email')} />
      <input className="sales-input" placeholder={t('salesJobTitle')}
             value={value.job_title || ''} onChange={set('job_title')} />
      <div className="sales-row">
        <input className="sales-input" placeholder={t('salesMobile')}
               value={value.mobile || ''} onChange={set('mobile')} />
        <input className="sales-input" placeholder={t('salesPhone')}
               value={value.phone || ''} onChange={set('phone')} />
      </div>

      {showRole && (
        <label className="sales-label sales-label--inline">
          {t('salesFiledAs')}
          <select className="sales-input" value={value.role || 'lead'} onChange={set('role')}>
            {/* The three ways the CRM files somebody against a company. Lead and
                decision maker both open a pipeline row this unit is working — the
                server suggests which from the job title, C-level/founders/owners
                being the decision-making unit and everybody else a lead — while an
                employee is only recorded as working there. The rep gets the last
                word: a wrongly filed decision maker displaces the person the unit
                actually wants to talk to. */}
            <option value="lead">{t('salesRoleLead')}</option>
            <option value="dmu">{t('salesRoleDmu')}</option>
            <option value="employee">{t('salesRoleEmployee')}</option>
          </select>
        </label>
      )}
    </div>
  );
};

export default PersonFields;
